import type { ReactNode } from 'react';

export type MotifId =
  | 'ai'
  | 'api'
  | 'automation'
  | 'building'
  | 'chart'
  | 'chat'
  | 'cloud'
  | 'code'
  | 'database'
  | 'design'
  | 'document'
  | 'integrate'
  | 'layers'
  | 'mobile'
  | 'rocket'
  | 'shield'
  | 'team'
  | 'voice'
  | 'web';

type MotifRule = { pattern: RegExp; motif: MotifId };

const MOTIF_RULES: MotifRule[] = [
  { pattern: /conversational|chatbot|chat|messaging|dialogue|copilot-assist/, motif: 'chat' },
  { pattern: /voice|speech|ivr|call-center|audio/, motif: 'voice' },
  { pattern: /generative|llm|gpt|agent|copilot|machine-learning|ml|ai-|artificial/, motif: 'ai' },
  { pattern: /security|trust|privacy|compliance|governance|responsible|ethical/, motif: 'shield' },
  { pattern: /mobile|ios|android|flutter|react-native|app-store/, motif: 'mobile' },
  { pattern: /frontend|react|vue|angular|nextjs|ui-ux|design-system|figma/, motif: 'design' },
  { pattern: /backend|api|microservice|graphql|rest-|node-|python-|java-|dotnet/, motif: 'api' },
  { pattern: /devops|kubernetes|docker|ci-cd|pipeline|infra/, motif: 'cloud' },
  { pattern: /cloud|aws|azure|gcp|serverless|hosting/, motif: 'cloud' },
  { pattern: /data|analytics|warehouse|bi-|etl|snowflake|databricks/, motif: 'database' },
  { pattern: /chart|metric|kpi|report|insight|dashboard/, motif: 'chart' },
  { pattern: /integrat|connect|sync|webhook|middleware/, motif: 'integrate' },
  { pattern: /automation|rpa|workflow|orchestrat/, motif: 'automation' },
  { pattern: /hire|staff|augment|dedicated|offshore|team/, motif: 'team' },
  { pattern: /career|recruit|talent/, motif: 'team' },
  { pattern: /industr|health|fintech|retail|logistic|manufactur|energy/, motif: 'building' },
  { pattern: /erp|crm|sap|salesforce|dynamics/, motif: 'building' },
  { pattern: /portfolio|case-study|platform|product-launch/, motif: 'rocket' },
  { pattern: /consult|contact|support|help|inquiry/, motif: 'document' },
  { pattern: /pricing|plan|subscription|billing/, motif: 'chart' },
  { pattern: /about|company|culture|values/, motif: 'team' },
  { pattern: /blog|resource|content|article/, motif: 'document' },
  { pattern: /privacy|legal|policy/, motif: 'shield' },
  { pattern: /software|develop|engineer|program|code|custom-app/, motif: 'code' },
  { pattern: /web|website|ecommerce|shopify|wordpress/, motif: 'web' },
];

const HUB_DEFAULT_MOTIF: Record<string, MotifId> = {
  services: 'layers',
  technologies: 'code',
  'ai-agents': 'ai',
  industries: 'building',
  hire: 'team',
  portfolio: 'rocket',
};

export function resolveMotifsFromPath(path: string): MotifId[] {
  const normalized = path.toLowerCase().replace(/\//g, ' ');
  const found: MotifId[] = [];

  for (const { pattern, motif } of MOTIF_RULES) {
    if (pattern.test(normalized) && !found.includes(motif)) {
      found.push(motif);
    }
    if (found.length >= 2) return found;
  }

  const hub = path.split('/').filter(Boolean)[0] ?? '';
  const fallback = HUB_DEFAULT_MOTIF[hub] ?? 'layers';
  if (!found.includes(fallback)) found.unshift(fallback);

  if (found.length < 2) {
    const padMotifs: MotifId[] = ['layers', 'chart', 'integrate', 'code', 'building'];
    const pad = padMotifs.find((m) => !found.includes(m));
    if (pad) found.push(pad);
  }

  return found.slice(0, 2);
}

type MotifRenderProps = { variant: number; gradId: string };

function offset(variant: number, axis: 'x' | 'y', spread = 24): number {
  const v = variant % 4;
  const deltas = axis === 'x' ? [0, spread, -spread, spread / 2] : [0, -spread / 2, spread / 2, spread];
  return deltas[v] ?? 0;
}

const MOTIF_RENDERERS: Record<MotifId, (p: MotifRenderProps) => ReactNode> = {
  chat: ({ variant, gradId }) => {
    const ox = offset(variant, 'x');
    return (
      <>
        <rect x={108 + ox} y={108} width={88} height={64} rx={14} stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
        <path d={`M${132 + ox} 132h40M${132 + ox} 152h24`} stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
        <rect x={224 - ox} y={124} width={72} height={48} rx={12} stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
        <path d={`M${244 - ox} 144h32`} stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.55" />
      </>
    );
  },
  voice: ({ variant, gradId }) => (
    <>
      <circle cx={200 + offset(variant, 'x')} cy={132} r={36} stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M184 168c8 16 24 16 32 0" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" />
      <path d="M248 108v48M264 124h-32" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  ai: ({ variant, gradId }) => (
    <>
      <circle cx={200} cy={128 + offset(variant, 'y', 12)} r={34} stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <circle cx={128} cy={176} r={18} stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-primary)" fillOpacity="0.12" />
      <circle cx={272} cy={176} r={18} stroke="var(--color-secondary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.12" />
      <path d="M168 128h64M168 148h40" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.6" />
    </>
  ),
  shield: ({ gradId }) => (
    <>
      <path d="M200 88l52 26v52l-52 26-52-26v-52l52-26z" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M184 152l14 14 36-44" stroke={`url(#${gradId})`} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  mobile: ({ variant, gradId }) => (
    <>
      <rect x={168 + offset(variant, 'x')} y={88} width={64} height={112} rx={14} stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <circle cx={200 + offset(variant, 'x')} cy={172} r={8} fill="var(--color-primary)" />
      <rect x={248} y={120} width={72} height={56} rx="10" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
    </>
  ),
  design: ({ gradId }) => (
    <>
      <rect x={104} y={104} width={120} height={88} rx="12" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <circle cx={152} cy={148} r={20} stroke="var(--color-secondary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.15" />
      <path d="M248 120h48v72h-48z" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
    </>
  ),
  api: ({ gradId }) => (
    <>
      <rect x={112} y={112} width={176} height={80} rx="12" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M136 136h32v32h-32zM232 128h32v40h-32z" stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.1" />
      <path d="M168 152h64" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 6" />
    </>
  ),
  cloud: ({ variant, gradId }) => (
    <>
      <path
        d={`M${148 + offset(variant, 'x')} 148c0-22 18-40 40-40 26 0 46 20 46 44H${132 + offset(variant, 'x')}c-18 0-32-14-32-32 0-16 12-28 28-30z`}
        stroke={`url(#${gradId})`}
        strokeWidth="2.5"
        fill="var(--semantic-bg-secondary)"
        fillOpacity="0.75"
      />
      <rect x={232} y={128} width={64} height={72} rx="10" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
    </>
  ),
  database: ({ gradId }) => (
    <>
      <ellipse cx={200} cy={120} rx={56} ry={20} stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <path d="M144 120v80c0 11 25 20 56 20s56-9 56-20v-80" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.55" />
      <path d="M144 160c0 11 25 20 56 20s56-9 56-20" stroke="var(--color-secondary)" strokeWidth="2" />
    </>
  ),
  chart: ({ variant, gradId }) => (
    <>
      <path d={`M${120 + offset(variant, 'x')} 200V${140 + (variant % 3) * 12}M${168 + offset(variant, 'x')} 200V${120 + (variant % 4) * 10}M${216} 200V${128 + (variant % 2) * 14}M${264} 200V${152 + (variant % 5) * 8}`} stroke={`url(#${gradId})`} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M108 200h184" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.35" />
    </>
  ),
  integrate: ({ gradId }) => (
    <>
      <circle cx={128} cy={148} r={22} stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-primary)" fillOpacity="0.12" />
      <circle cx={272} cy={148} r={22} stroke="var(--color-secondary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.12" />
      <path d="M150 148h100" stroke={`url(#${gradId})`} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M188 132l12 16 12-16M188 164l12-16 12 16" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  automation: ({ gradId }) => (
    <>
      <rect x={120} y={108} width={160} height={88} rx="14" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M152 140h48l24 24H176l-24-24z" stroke="var(--color-primary)" strokeWidth="2" strokeLinejoin="round" />
      <circle cx={248} cy={140} r={12} fill="var(--color-secondary)" fillOpacity="0.35" />
    </>
  ),
  team: ({ gradId }) => (
    <>
      <circle cx={160} cy={148} r={24} stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-primary)" fillOpacity="0.12" />
      <circle cx={240} cy={148} r={24} stroke="var(--color-secondary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.12" />
      <path d="M120 200h160" stroke={`url(#${gradId})`} strokeWidth="2.5" strokeLinecap="round" />
    </>
  ),
  building: ({ gradId }) => (
    <>
      <path d="M108 200V116h48v84M180 200V96h48v104M252 200v-60h48v60" stroke={`url(#${gradId})`} strokeWidth="2.5" strokeLinecap="round" fill="var(--semantic-bg-secondary)" fillOpacity="0.55" />
      <path d="M96 200h208" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
    </>
  ),
  document: ({ gradId }) => (
    <>
      <rect x={144} y={96} width={112} height={128} rx="12" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <path d="M168 128h64M168 152h48M168 176h56" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  rocket: ({ gradId }) => (
    <>
      <path d="M200 88l32 80-32 24-32-24 32-80z" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M168 200c16-12 48-12 64 0" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.4" />
      <circle cx={200} cy={200} r={10} fill="var(--color-secondary)" fillOpacity="0.4" />
    </>
  ),
  code: ({ gradId }) => (
    <>
      <rect x={104} y={100} width={192} height={104} rx="14" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M136 132l-16 16 16 16M264 132l16 16-16 16M208 128l-24 48" stroke={`url(#${gradId})`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  web: ({ gradId }) => (
    <>
      <rect x={96} y={108} width={208} height={96} rx="12" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M96 132h208" stroke="var(--color-secondary)" strokeWidth="2" />
      <circle cx={120} cy={120} r={4} fill="var(--color-primary)" />
      <circle cx={136} cy={120} r={4} fill="var(--color-secondary)" />
    </>
  ),
  layers: ({ variant, gradId }) => (
    <>
      <rect x={108 + offset(variant, 'x')} y={120} width={96} height={72} rx="10" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
      <rect x={140 + offset(variant, 'x', 16)} y={96} width={96} height={72} rx="10" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <rect x={172 + offset(variant, 'x', 8)} y={136} width={96} height={72} rx="10" stroke={`url(#${gradId})`} strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
    </>
  ),
};

export function renderMotifLayers(motifs: MotifId[], variant: number, gradId: string): ReactNode {
  return motifs.map((motif, index) => {
    const render = MOTIF_RENDERERS[motif] ?? MOTIF_RENDERERS.layers;
    return (
      <g key={`${motif}-${index}`}>
        {render({ variant, gradId })}
      </g>
    );
  });
}
